import { useEffect, useState } from 'react';
import { api, type CompanySection, type Personnel, type CodeLibRow } from '../api/client';

// Admin (§5.20). Company Profile content (proposals pull from here — Rule 2),
// personnel dropdown options, and the codes & standards library. Edits are
// enforced server-side (admin.settings); non-admins get a 403.
export function Admin() {
  const [sections, setSections] = useState<CompanySection[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [codes, setCodes] = useState<CodeLibRow[]>([]);
  const [error, setError] = useState('');

  async function load() {
    try {
      const [cp, pn, cd] = await Promise.all([api.getCompanyProfile(), api.getPersonnel(), api.getCodes()]);
      setSections(cp.sections); setPersonnel(pn.personnel); setCodes(cd.codes);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }
  useEffect(() => { void load(); }, []);

  return (
    <div>
      <h1>Admin Settings</h1>
      <p className="muted">Company Profile, personnel and the codes library. Only admins can edit.</p>
      {error && <div className="error">{error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Company Profile <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— proposals assemble from these blocks; the software invents nothing</span></h2>
        {sections.map((s) => <ProfileEditor key={s.section} section={s} onSaved={load} setError={setError} />)}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Personnel</h2>
        <PersonnelEditor personnel={personnel} onChange={load} setError={setError} />
      </div>

      <div className="card">
        <h2>Codes & Standards library <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({codes.length})</span></h2>
        <CodesEditor codes={codes} onChange={load} setError={setError} />
      </div>
    </div>
  );
}

function ProfileEditor({ section, onSaved, setError }: { section: CompanySection; onSaved: () => void; setError: (s: string) => void }) {
  const [body, setBody] = useState(section.body);
  const [saving, setSaving] = useState(false);
  const dirty = body !== section.body;
  async function save() {
    setSaving(true);
    try { await api.updateCompanyProfile(section.section, { body }); onSaved(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Save failed (admin only)'); }
    finally { setSaving(false); }
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>{section.title}</strong>
        {section.is_placeholder ? <span className="pill pill-needs_review">placeholder</span> : <span className="pill pill-confirmed">edited</span>}
      </div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
        style={{ width: '100%', padding: 8, border: '1px solid var(--gray-300)', borderRadius: 6, font: 'inherit', marginTop: 4 }} />
      {dirty && <button className="btn btn-ghost btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
    </div>
  );
}

function PersonnelEditor({ personnel, onChange, setError }: { personnel: Personnel[]; onChange: () => void; setError: (s: string) => void }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('estimator');
  async function add() {
    if (!name.trim()) return;
    try { await api.addPersonnel({ name: name.trim(), role }); setName(''); onChange(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Add failed (admin only)'); }
  }
  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="estimator">Estimator</option><option value="salesperson">Salesperson</option>
          <option value="project_manager">Project Manager</option><option value="engineer">Engineer</option>
        </select>
        <button className="btn btn-ghost btn-sm" onClick={add}>Add</button>
      </div>
      <table style={{ marginTop: 10 }}>
        <tbody>
          {personnel.map((p) => (
            <tr key={p.id}><td><strong>{p.name}</strong></td><td className="muted">{p.role}</td>
              <td><button className="btn btn-ghost btn-sm" onClick={async () => { try { await api.deletePersonnel(p.id); onChange(); } catch (e) { setError(`${e}`); } }}>✕</button></td></tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function CodesEditor({ codes, onChange, setError }: { codes: CodeLibRow[]; onChange: () => void; setError: (s: string) => void }) {
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [level, setLevel] = useState('federal');
  async function add() {
    if (!code.trim()) return;
    try { await api.addCode({ level, code: code.trim(), title }); setCode(''); setTitle(''); onChange(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Add failed (admin only)'); }
  }
  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="federal">Federal</option><option value="state">State</option>
          <option value="county">County</option><option value="trade">Trade</option>
        </select>
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code (e.g. NFPA 72)" />
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" style={{ flex: 1, minWidth: 160 }} />
        <button className="btn btn-ghost btn-sm" onClick={add}>Add</button>
      </div>
      <table style={{ marginTop: 10 }}>
        <thead><tr><th>Level</th><th>Code</th><th>Title</th><th>Applies to</th></tr></thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.id}><td className="muted">{c.level}</td><td><strong>{c.code}</strong></td><td>{c.title}</td><td className="muted">{c.applies_to_system}</td></tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
